/**
 * Create share (plan Task 7.16): the name follows `shareNameProblem`, the optional quota `quotaProblem` with the
 * connection's file account facts (the model's range — or the "the service decides" sentence — under the field), and
 * the protocol choice appears for a FileStorage account (SMB default; NFS explains its network requirement) and, when
 * the account kind is unknown, with the sentence that NFS needs FileStorage. Microsoft Entra administration is
 * attempted (I2): a 403 shows main's role sentence with its chip. A duplicate answers `ShareAlreadyExists` under the
 * name. TanStack Form; one submit in flight; closing while it runs cancels it in main.
 */
import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { shareNameProblem } from '../../../../../core/engines/azureStorageFilePaths';
import { billingSentence, quotaProblem } from '../../../../../core/engines/azureStorageShareQuota';
import type { FileAccountFacts, FileShareProtocol, StorageOperationError } from '../../../../../types/azureStorage';
import type { ShareDialogRequest } from '../fileDialogStore';
import { startShareRequest } from '../shareApi';
import { shareActionOutcome, trackShareAction } from '../shareActions';
import { quotaRangeText, useFileAccountFacts } from './fileAccountFacts';
import { ShareErrorNotice } from './ShareErrorNotice';
import { useShareRefusal } from './shareGate';
import { useShareOperation } from './useShareOperation';
import { StorageAccountName } from '../../components/StorageAccountName';

export const NFS_NEEDS_FILESTORAGE_NOTE = 'NFS shares need a premium FileStorage account; Azure refuses NFS elsewhere.';
export const NFS_NETWORK_NOTE = 'NFS shares need a virtual network to mount; Jam SQL browses them over REST.';

type Props = { request: ShareDialogRequest; onClose: () => void };
type Values = { name: string; quota: string; protocol: FileShareProtocol };

/** An empty field sends no quota (the service default); anything else must pass the model's bounds. */
const quotaFieldProblem = (text: string, facts: FileAccountFacts): string | undefined => (text.trim() ? quotaProblem(Number(text.trim()), facts) : undefined);

export function CreateShareDialog({ request, onClose }: Props) {
  const facts = useFileAccountFacts(request.connectionId);
  const refusal = useShareRefusal(request.connectionId)('create-share');
  const kind = facts.kind?.toLowerCase();
  const showProtocol = kind === undefined || kind === 'filestorage';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<StorageOperationError>();
  const [exists, setExists] = useState<string>();
  const { begin, settle, abandon, isActive } = useShareOperation();
  const form = useForm({
    defaultValues: { name: '', quota: '', protocol: 'smb' } as Values,
    onSubmit: async ({ value }) => {
      const share = value.name.trim();
      if (isActive() || refusal || shareNameProblem(share) || quotaFieldProblem(value.quota, facts)) return;
      const protocol: FileShareProtocol = showProtocol ? value.protocol : 'smb';
      const meta = { source: request.source, protocol };
      setPending(true);
      setError(undefined);
      setExists(undefined);
      const handle = startShareRequest('shareCreate', {
        connectionId: request.connectionId, share,
        ...(value.quota.trim() ? { quotaGiB: Number(value.quota.trim()) } : {}),
        ...(showProtocol ? { protocol: value.protocol } : {}),
      });
      begin(handle, 'create', meta);
      const result = await handle.promise;
      if (!settle(handle)) return;
      setPending(false);
      trackShareAction('create', shareActionOutcome(result.ok ? undefined : result.error), meta);
      if (result.ok) { onClose(); return; }
      if (result.error.code === 'ShareAlreadyExists') setExists(result.error.message);
      else setError(result.error);
    },
  });
  const dismiss = () => { abandon(); onClose(); };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent data-testid="create-share-dialog">
        <DialogHeader>
          <DialogTitle>Create file share</DialogTitle>
          <DialogDescription>Create an Azure file share in storage account <StorageAccountName connectionId={request.connectionId} />.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }}>
          {refusal && <p className="text-sm text-muted-foreground" data-testid="create-share-refusal">{refusal}</p>}
          <form.Field name="name">{(field) => {
            const problem = field.state.value.trim() ? shareNameProblem(field.state.value.trim()) : undefined;
            return (
              <div className="space-y-1">
                <Label htmlFor="create-share-name">Share name</Label>
                <Input spellCheck={false} autoComplete="off" id="create-share-name" value={field.state.value} autoFocus data-testid="create-share-name" onChange={(event) => { setExists(undefined); field.handleChange(event.target.value); }} />
                {problem && <p className="text-xs text-destructive" data-testid="create-share-name-problem">{problem}</p>}
                {exists && <p className="text-xs text-destructive" data-testid="create-share-name-exists">{exists}</p>}
              </div>
            );
          }}</form.Field>
          <form.Field name="quota">{(field) => {
            const problem = quotaFieldProblem(field.state.value, facts);
            const billing = billingSentence(facts.billing);
            return (
              <div className="space-y-1">
                <Label htmlFor="create-share-quota">Quota (GiB, optional)</Label>
                <Input id="create-share-quota" inputMode="numeric" value={field.state.value} data-testid="create-share-quota" onChange={(event) => field.handleChange(event.target.value)} />
                <p className="text-xs text-muted-foreground" data-testid="create-share-quota-range">{quotaRangeText(facts)} Leave empty for the service default.</p>
                {billing && <p className="text-xs text-muted-foreground" data-testid="create-share-billing">{billing}</p>}
                {problem && <p className="text-xs text-destructive" data-testid="create-share-quota-problem">{problem}</p>}
              </div>
            );
          }}</form.Field>
          {showProtocol && <form.Field name="protocol">{(field) => (
            <div className="space-y-1">
              <Label>Protocol</Label>
              <RadioGroup value={field.state.value} onValueChange={(next) => field.handleChange(next as FileShareProtocol)} className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="smb" data-testid="create-share-protocol-smb" />SMB</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="nfs" data-testid="create-share-protocol-nfs" />NFS</label>
              </RadioGroup>
              {kind === undefined && <p className="text-xs text-muted-foreground" data-testid="create-share-protocol-note">{NFS_NEEDS_FILESTORAGE_NOTE}</p>}
              {field.state.value === 'nfs' && <p className="text-xs text-muted-foreground" data-testid="create-share-nfs-note">{NFS_NETWORK_NOTE}</p>}
            </div>
          )}</form.Field>}
          {error && <ShareErrorNotice error={error} testId="create-share-error" />}
          <form.Subscribe selector={(state) => [state.values.name, state.values.quota] as const}>{([name, quota]) => {
            const invalid = !name.trim() || Boolean(shareNameProblem(name.trim())) || Boolean(quotaFieldProblem(quota, facts));
            return (
              <DialogFooter>
                <Button type="button" variant="outline" onClick={dismiss} data-testid="create-share-cancel">Cancel</Button>
                <ReasonButton type="submit" reason={refusal} disabled={pending || invalid} data-testid="create-share-submit">
                  {pending && <RandomDotmSpinner size={14} className="mr-1" />}Create share
                </ReasonButton>
              </DialogFooter>
            );
          }}</form.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  );
}
